import {Tooltip} from "@/components/Common/Tooltip";
import {useEffect, useRef, useState, type ComponentProps, type FC} from "react";
import type {TooltipRefProps} from "react-tooltip";

const armCallbacks = new Map<string, Set<() => void>>();

const armTooltip = (event: Event) => {
  const anchor = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-tooltip-id]") : null;
  const id = anchor?.dataset.tooltipId;

  if (id) armCallbacks.get(id)?.forEach(arm => arm());
};

document.addEventListener("mouseover", armTooltip);
document.addEventListener("focusin", armTooltip);

const LazyTooltip: FC<ComponentProps<typeof Tooltip> & {id: string}> = props => {
  const {delayShow, id} = props;
  const anchorSelect = `[data-tooltip-id='${id}']`;
  const tooltipRef = useRef<TooltipRefProps>(null);
  const [isArmed, setIsArmed] = useState(false);

  useEffect(() => {
    if (isArmed) return;

    const arm = () => setIsArmed(true);
    const callbacks = armCallbacks.get(id) ?? new Set();
    armCallbacks.set(id, callbacks.add(arm));

    return () => {
      callbacks.delete(arm);
      if (!callbacks.size) armCallbacks.delete(id);
    };
  }, [id, isArmed]);

  useEffect(() => {
    if (isArmed && document.querySelector(`${anchorSelect}:is(:hover, :focus)`)) {
      tooltipRef.current?.open({anchorSelect, delay: delayShow});
    }
  }, [isArmed]);

  return isArmed ? <Tooltip {...props} ref={tooltipRef} /> : null;
};

export default LazyTooltip;
